const steps = {
    up: { i: 0, j: -1 },
    down: { i: 0, j: 1 },
    right: { i: 1, j: 0 },
    left: { i: -1, j: 0 }
};

export const Directions = {
    UP: 'up',
    DOWN: 'down',
    RIGHT: 'right',
    LEFT: 'left'
};

export const FlippedDirections = {
    [Directions.UP]: 'left',
    [Directions.DOWN]: 'right',
    [Directions.RIGHT]: 'down',
    [Directions.LEFT]: 'up'
};

export const flipDirection = (orientation) => {
    const arr = orientation.split('-to-');

    return arr.map((s) => FlippedDirections[s]).join('-to-');
};

export class Snake {
    #head = 0;
    #tail = 0;
    #bodyStart = 0;
    #speed = 0;
    #direction = null;
    #directionTaken = null;
    #step = { i: 0, j: 0 };
    #stepTaken = { i: 0, j: 0 };
    #minSpeed = 0;
    #maxSpeed = 0;
    #corner = null;
    #changed = true;
    #lastCellsRemoved = null;
    #alive = true;
    #lastUpdated = -1;
    #w = 10;
    #h = 10;
    #cells = [];
    #rows = [];
    #incrementBody = 0;
    #incrementSpeed = 0;

    constructor({
        head, tail, bodyStart, minSpeed, maxSpeed
    }, { w, h }) {
        this.#head = head + 1; // 1 extra transparent cell
        this.#tail = tail;
        this.#w = w;
        this.#h = h;
        this.#bodyStart = bodyStart;
        this.#minSpeed = minSpeed;
        this.#maxSpeed = maxSpeed;

        this.reset();
    }

    reset() {
        this.#speed = this.#minSpeed;
        this.#lastCellsRemoved = null;
        this.#lastUpdated = -1;
        this.#alive = true;
        this.#cells = [];
        this.#rows = [];
    }

    readySteady() {
        const step = Math.random() > 0.5 ? 1 : -1;
        const iindex = Math.ceil(this.#w * (step > 1 ? 0.65 : 0.35));
        const jindex = Math.ceil(this.#h * 0.5);

        this.#step = { i: -step, j: 0 };
        this.#stepTaken = this.#step;
        this.#direction = step > 0 ? Directions.LEFT : Directions.RIGHT;
        this.#directionTaken = this.#direction;

        this.#prepareRows();
        this.#fillUpALine(iindex, jindex, this.#bodyStart, step);
    }

    go() {
        this.#lastUpdated = Date.now();
    }

    #prepareRows() {
        this.#rows = new Array(this.#h).fill(0).map(() => []);
    }

    #fillUpALine(iindex, jindex, body, step) {
        let i;
        let p0 = iindex;

        const increment = () => {
            p0 += step;
            if (p0 > this.#w - 1) p0 = 0;
            else if (p0 < 0) p0 = this.#w - 1;
        };

        for (i = 0; i < this.#head + body + this.#tail; i += 1) {
            const c = {
                i: p0, j: jindex, step: { i: -step, j: 0 }, orientation: this.#direction
            };

            const row = this.#rows[jindex];

            if (step > 0) {
                row.push(c);
            } else {
                row.unshift(c);
            }

            this.#cells.push(c);

            increment();
        }

        // console.log(this.#cells);
    }

    invalidate(t) {
        if (this.#lastUpdated < 0 || this.#cells.length < 1) {
            this.#changed = false;

            return;
        }
        if (t - this.#lastUpdated < (200 / this.#speed)) {
            this.#changed = false;

            return;
        }

        let removed = null;
        let { i, j } = this.#cells[0];
        const { i: di, j: dj } = this.#step;

        i += di;
        j += dj;

        // Cycle X
        if (i > this.#w - 1) i = 0;
        else if (i < 0) i = this.#w - 1;

        // Cycle Y
        if (j > this.#h - 1) j = 0;
        else if (j < 0) j = this.#h - 1;

        if (this.#corner) {
            this.#cells[0].orientation = this.#corner;
            // RESET corner
            this.#corner = null;
        }

        const newCell = {
            i, j, step: this.#step, orientation: this.#direction
        };

        this.#cells.unshift(newCell);
        this.#stepTaken = this.#step;
        this.#directionTaken = this.#direction;

        // Save to rows:
        const row = this.#rows[j];
        const iHiggher = row.findIndex((el) => el.i > i);

        if (iHiggher < 0) {
            row.push(newCell);
        } else {
            row.splice(iHiggher, 0, newCell);
        }

        if (this.#incrementBody !== 0) {
            // this.#incrementBody > 0 then do nothing
            if (this.#incrementBody < 0) {
                removed = this.#cells.splice(this.#cells.length - 2, 2);
            }
            this.#incrementBody = 0;
        } else {
            removed = [this.#cells.pop()];
        }

        if (removed instanceof Array) {
            removed.forEach(({ i: i1, j: j1 }) => {
                const elRow = this.#rows[j1];

                this.#rows[j1] = elRow.filter(({ i: i2 }) => i2 !== i1);
            });
        }

        if (this.#incrementSpeed !== 0) {
            this.#speed = Math.min(Math.max(this.#speed + this.#incrementSpeed, this.#minSpeed), this.#maxSpeed);
            this.#incrementSpeed = 0;
        }

        const { intersectionIndex, alive } = this.#isAlive();

        if (intersectionIndex > 0) this.#makeIntersection(intersectionIndex);
        this.#alive = alive;

        this.#lastCellsRemoved = this.#lastCellsRemoved ? [...this.#lastCellsRemoved, ...removed] : removed;
        this.#lastUpdated = t;

        this.#changed = true;
    }

    #makeIntersection(index) {
        // const [hcell] = this.#cells;
        const cell = this.#cells[index];

        cell.isBounced = true;
    }

    #isAlive() {
        if (!this.#cells) return { intersectionIndex: -1, alive: false };

        const { length } = this.#cells;

        if (length < this.head + this.tail) return { intersectionIndex: -1, alive: false };

        const [hcell, ...rest] = this.#cells;
        let intersectionIndex = rest
            .findIndex((cell) => (hcell.i === cell.i && hcell.j === cell.j));

        intersectionIndex += 1;

        return { intersectionIndex, alive: intersectionIndex < 1 };
    }

    isAlive() {
        return this.#alive;
    }

    resetChanged() {
        this.#changed = false;
        this.#lastCellsRemoved = null;
    }

    get head() {
        return this.#head;
    }

    get tail() {
        return this.#tail;
    }

    get cells() {
        return this.#cells;
    }

    get lastCellsRemoved() {
        return this.#lastCellsRemoved;
    }

    set incrementBody(value) {
        this.#incrementBody += value;
    }

    get incrementBody() {
        return this.#incrementBody;
    }

    set incrementSpeed(value) {
        this.#incrementSpeed += value;
    }

    get incrementSpeed() {
        return this.#incrementSpeed;
    }

    set direction(value) {
        const step = steps[value];

        if (!step) {
            console.warn('There\'s no such a direction declared');

            return;
        }

        if (step.i === this.#stepTaken.i && step.j === this.#stepTaken.j) return;
        if (step.i === -this.#stepTaken.i && step.j === -this.#stepTaken.j) return;

        this.#corner = `${this.#directionTaken}-to-${value}`;
        // console.log(value, this.#corner, this.#direction);
        this.#direction = value;
        this.#step = step;
    }

    get direction() {
        return this.#directionTaken;
    }

    set w(value) {
        this.#w = value;
    }

    get w() {
        return this.#w;
    }

    set h(value) {
        this.#h = value;
    }

    get h() {
        return this.#h;
    }

    get running() {
        return this.#lastUpdated > 0;
    }

    get rows() {
        // Fired only for playground items generation
        return this.#rows.map((arr) => [...(arr/* .map(({ i, j }) => ({ i, j })) */)]);
    }
}
